import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { trackFeature } from '../services/telemetryService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEnvelope,
  faLock,
  faUser,
  faArrowRight,
  faShieldHalved,
  faUserCheck,
  faLightbulb,
  faEye,
  faEyeSlash,
  faExclamationCircle,
  faCheckCircle,
  faHeadset,
} from '@fortawesome/free-solid-svg-icons';
import { faGoogle, faApple } from '@fortawesome/free-brands-svg-icons';
import { authAPI } from '../services/api';
import { setCredentials } from '../store/slices/authSlice';
import { useI18n } from '../hooks/useI18n';
import AuthLangSwitcher from '../components/AuthLangSwitcher';
import { openMailClient } from '../utils/mailSupport';
import '../styles/AuthModern.css';

const SignupModern: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useI18n();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError('');
  };

  // Password strength logic
  const passwordStrength = useMemo(() => {
    const pw = formData.password;
    if (!pw) return { level: 0, label: '', key: '' };
    let score = 0;
    if (pw.length >= 8) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    if (score <= 1) return { level: 1, label: t('auth.weak'), key: 'weak' };
    if (score === 2) return { level: 2, label: t('auth.fair'), key: 'fair' };
    if (score === 3) return { level: 3, label: t('auth.good'), key: 'good' };
    return { level: 4, label: t('auth.strong'), key: 'strong' };
  }, [formData.password, t]);

  const handleGoogleLogin = async () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
    if (!clientId) {
      setError('Google Sign-In is not configured.');
      return;
    }
    if (!window.google?.accounts?.id) {
      setError('Google Sign-In library not loaded. Please refresh the page.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async (response: any) => {
          try {
            const result = await authAPI.loginWithGoogle(response.credential);
            if (result.data?.token && result.data?.user) {
              dispatch(setCredentials({ user: result.data.user, token: result.data.token }));
            }
            trackFeature('signup_google_success');
            navigate('/');
          } catch (err: any) {
            setError(err.response?.data?.error || 'Google Sign-Up failed. Please try again.');
          } finally {
            setLoading(false);
          }
        },
        auto_select: false,
        cancel_on_tap_outside: true,
      });
      window.google.accounts.id.prompt((notification: any) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
            `client_id=${encodeURIComponent(clientId)}` +
            `&redirect_uri=${encodeURIComponent(window.location.origin + '/auth/google/callback')}` +
            `&response_type=token%20id_token` +
            `&scope=openid%20email%20profile` +
            `&nonce=${Math.random().toString(36).substring(2)}`;
          window.location.href = authUrl;
        }
      });
    } catch (err: any) {
      setError('Google Sign-Up failed. Please try again.');
      setLoading(false);
    }
  };

  const handleAppleLogin = async () => {
    setError('Apple Sign-In is not available.');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.firstName || !formData.email || !formData.password || !formData.confirmPassword) {
      setError(t('auth.fillAllFields'));
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.passwordMismatch'));
      return;
    }
    if (formData.password.length < 8) {
      setError(t('auth.passwordTooShort'));
      return;
    }

    // CAPTCHA is optional — don't block signup if it failed or timed out
    // Token will be sent to backend if available; backend also treats it as optional

    setLoading(true);
    try {
      const response = await authAPI.signup(
        formData.email,
        formData.password,
        formData.firstName,
        formData.lastName,
        captchaToken || undefined
      );

      if (response.data?.token && response.data?.user) {
        // Server sets session cookie automatically; also store in Redux for UI state
        dispatch(setCredentials({ user: response.data.user, token: response.data.token }));
      } else if (response.data?.token) {
        localStorage.setItem('token', response.data.token);
      }

      setSuccess(t('auth.signupSuccess'));
      trackFeature('signup_success');
      setTimeout(() => {
        navigate('/verify-email', { state: { email: formData.email } });
      }, 2000);
    } catch (err: any) {
      if (err.response?.data?.code === 'EMAIL_EXISTS' || err.response?.status === 409) {
        setError(t('auth.emailAlreadyExists'));
      } else if (!err.response) {
        setError(t('auth.networkError'));
      } else {
        setError(err.response?.data?.error || t('auth.signupFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-wrapper">
        {/* Left Branding Panel */}
        <div className="auth-brand-panel">
          <div className="auth-brand-logo">
            <div className="auth-brand-logo-icon">E</div>
            <div className="auth-brand-logo-text">ELVOAQ</div>
          </div>

          <h2 className="auth-brand-heading">
            {t('auth.joinCommunity').split(' ').slice(0, 2).join(' ')}{' '}
            <span>{t('auth.joinCommunity').split(' ').slice(2).join(' ')}</span>
          </h2>
          <p className="auth-brand-desc">{t('auth.signUpSubtitle')}</p>

          <div className="auth-features-list">
            <div className="auth-feature-item">
              <div className="auth-feature-icon">
                <FontAwesomeIcon icon={faShieldHalved} />
              </div>
              <span className="auth-feature-text">{t('auth.benefitSecure')}</span>
            </div>
            <div className="auth-feature-item">
              <div className="auth-feature-icon">
                <FontAwesomeIcon icon={faUserCheck} />
              </div>
              <span className="auth-feature-text">{t('auth.benefitVerified')}</span>
            </div>
            <div className="auth-feature-item">
              <div className="auth-feature-icon">
                <FontAwesomeIcon icon={faLightbulb} />
              </div>
              <span className="auth-feature-text">{t('auth.benefitSmart')}</span>
            </div>
          </div>
        </div>

        {/* Right Form Panel */}
        <div className="auth-form-panel">
          <div className="auth-card">
            <AuthLangSwitcher />

            <div className="auth-form-header">
              <h1>{t('auth.createAccount')}</h1>
              <p>{t('auth.signUpSubtitle')}</p>
            </div>

            {error && (
              <div className="auth-error">
                <FontAwesomeIcon icon={faExclamationCircle} />
                <span>{error}</span>
              </div>
            )}
            {success && (
              <div className="auth-success">
                <FontAwesomeIcon icon={faCheckCircle} />
                <span>{success}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="auth-form">
              <div className="auth-form-row">
                <div className="auth-form-group">
                  <label className="auth-form-label">
                    <FontAwesomeIcon icon={faUser} />
                    {t('auth.firstName')}
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    className="auth-input"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder={t('auth.firstNamePlaceholder')}
                    disabled={loading}
                    autoComplete="given-name"
                    autoFocus
                  />
                </div>
                <div className="auth-form-group">
                  <label className="auth-form-label">
                    <FontAwesomeIcon icon={faUser} />
                    {t('auth.lastName')}
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    className="auth-input"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder={t('auth.lastNamePlaceholder')}
                    disabled={loading}
                    autoComplete="family-name"
                  />
                </div>
              </div>

              <div className="auth-form-group">
                <label className="auth-form-label">
                  <FontAwesomeIcon icon={faEnvelope} />
                  {t('auth.email')}
                </label>
                <input
                  type="email"
                  name="email"
                  className="auth-input"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder={t('auth.emailPlaceholder')}
                  disabled={loading}
                  autoComplete="email"
                />
              </div>

              <div className="auth-form-group">
                <label className="auth-form-label">
                  <FontAwesomeIcon icon={faLock} />
                  {t('auth.password')}
                </label>
                <div className="auth-input-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    className="auth-input"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder={t('auth.passwordPlaceholder')}
                    disabled={loading}
                    autoComplete="new-password"
                    style={{ paddingRight: '50px' }}
                  />
                  <button
                    type="button"
                    className="auth-input-icon"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                  </button>
                </div>
                {formData.password && (
                  <div className="password-strength">
                    <div className="password-strength-bars">
                      {[1, 2, 3, 4].map((i) => (
                        <div
                          key={i}
                          className={`password-strength-bar${i <= passwordStrength.level ? ` active ${passwordStrength.key}` : ''}`}
                        />
                      ))}
                    </div>
                    <span className={`password-strength-text ${passwordStrength.key}`}>
                      {passwordStrength.label}
                    </span>
                  </div>
                )}
              </div>

              <div className="auth-form-group">
                <label className="auth-form-label">
                  <FontAwesomeIcon icon={faLock} />
                  {t('auth.confirmPassword')}
                </label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  className="auth-input"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder={t('auth.passwordPlaceholder')}
                  disabled={loading}
                  autoComplete="new-password"
                />
              </div>

              <button type="submit" className="auth-submit-btn" disabled={loading}>
                {loading ? (
                  <>
                    <span className="auth-spinner" />
                    {t('auth.signingUp')}
                  </>
                ) : (
                  <>
                    {t('auth.signUp')}
                    <FontAwesomeIcon icon={faArrowRight} />
                  </>
                )}
              </button>
            </form>

            <div className="auth-divider">
              <span>or sign up with</span>
            </div>

            <div className="auth-social-buttons">
              <button
                type="button"
                className="auth-social-btn google"
                onClick={handleGoogleLogin}
                disabled={loading}
              >
                <FontAwesomeIcon icon={faGoogle} />
                Sign up with Google
              </button>
            </div>

            <div className="auth-terms">
              {t('auth.agreeTerms')}{' '}
              <a href="/terms">{t('auth.termsOfService')}</a>{' '}
              {t('auth.and')}{' '}
              <a href="/privacy">{t('auth.privacyPolicy')}</a>
            </div>

            <div className="auth-footer-link">
              {t('auth.hasAccount')}{' '}
              <button type="button" onClick={() => navigate('/login')}>
                {t('auth.signInLink')}
              </button>
            </div>

            <div className="auth-support-section">
              <p className="auth-support-text">{t('auth.havingProblems')}</p>
              <button
                type="button"
                className="auth-support-btn"
                onClick={() => openMailClient(t('auth.supportSubject'), t('auth.supportBody'))}
              >
                <FontAwesomeIcon icon={faHeadset} />
                {t('auth.reachOutToUs')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupModern;
